import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Search, ChevronRight, Check, Receipt, Sparkles, Calendar, Laptop, X } from 'lucide-react';
import { PickerOptionIcon } from './pickerOptionVisuals';
import AnchoredOverlay from './AnchoredOverlay';
import Button from './Button';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { API_URL } from '../../config/constants';
import { resolveCategoryCreateMutationOutcome } from '../../utils/categoryMutationState';
import {
  buildCreatedCategorySelectionState,
  mergeOptimisticCategories,
  mergeOptimisticSections,
  normalizePickerSectionOption,
} from '../../utils/categoryPickerState';
import {
  getDirectCategoryMatches,
  getSimilarCategorySuggestions,
  hasExactCategoryNameMatch,
  hasStrongCategoryMatch,
} from './categorySearchModel';
import {
  buildMutationIntentStorageKey,
  clearMutationIntent,
  createMutationContractError,
  getOrCreateMutationIntent,
  shouldRetainMutationIntent,
} from '../../utils/mutationContracts';

const SEARCH_SETTLE_MS = 250;
const MIN_CREATE_QUERY_LENGTH = 2;
const DEFAULT_PICKER_HEIGHT = 420;
export const REVIEW_PICKER_THEME_VARS = {
  '--color-bg': '#1a2437',
  '--color-surface': '#1a2335',
  '--color-surface-input': '#182137',
  '--color-surface-elevated': '#1a2332',
  '--color-surface-row': 'rgba(15, 23, 42, 0.2)',
  '--color-border': 'rgba(51, 65, 85, 0.5)',
  '--color-border-strong': 'rgba(255, 255, 255, 0.1)',
  '--color-text-primary': '#ffffff',
  '--color-text-secondary': 'rgba(255, 255, 255, 0.9)',
  '--color-text-muted': 'rgba(255, 255, 255, 0.6)',
  '--color-surface-hover': 'rgba(255, 255, 255, 0.04)',
  '--color-surface-selected': 'rgba(255, 255, 255, 0.1)',
  '--color-surface-selected-hover': 'rgba(255, 255, 255, 0.12)',
  '--shadow-card': '0 20px 40px rgba(0, 0, 0, 0.6), 0 1px 2px rgba(0, 0, 0, 0.3)',
};
const CREATE_GROUP_ICONS = {
  Fixed: Receipt,
  Flexible: Sparkles,
  'Non-Monthly': Calendar,
  'Digital Subscriptions': Laptop,
  Excluded: X,
};

const getCreateGroupIcon = (groupName) => CREATE_GROUP_ICONS[groupName] || ChevronRight;

/**
 * CategoryPicker - A search-first command-palette style category selector
 *
 * Features:
 * - Search input with auto-focus
 * - Categories grouped by parent when no search
 * - Filtered results with full path when searching
 * - Keyboard navigation (arrows, enter, esc)
 * - Mouse selection support
 */
const CategoryPicker = ({
  categories,
  sections = null,
  value,
  onChange,
  onClose,
  style,
  searchPlaceholder = 'Search categories...',
  emptyMessage = 'No categories found',
  footer = null,
  onPreviewValueChange,
  allowCreate = false,
  householdId = null,
  actorUserId = null,
  authenticatedFetch = null,
  onCategoryCreated = null,
  forceDarkMode = false,
}) => {
  const [search, setSearch] = useState('');
  const [settledSearch, setSettledSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [previewValue, setPreviewValue] = useState(String(value || ''));
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [createName, setCreateName] = useState('');
  const [selectedParentId, setSelectedParentId] = useState('');
  const [createError, setCreateError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [hasLatchedCreateState, setHasLatchedCreateState] = useState(false);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);
  const itemRefs = useRef([]);
  const createCategoryIntentRef = useRef(null);
  const createCategoryIntentStorageKey = buildMutationIntentStorageKey({
    operation: 'category-picker-create', actorUserId, householdId,
  });

  const resolvedSections = useMemo(() => {
    if (Array.isArray(sections) && sections.length > 0) {
          return sections.map((section) => ({
        ...section,
        options: (Array.isArray(section.options) ? section.options : []).map((option) =>
          normalizePickerSectionOption(option, section.name)
        ),
      }));
    }

    const parents = categories.filter((category) => !category.parent_id);
    const childrenByParent = {};
    categories.forEach((category) => {
      if (!category.parent_id) return;
      if (!childrenByParent[category.parent_id]) childrenByParent[category.parent_id] = [];
      childrenByParent[category.parent_id].push(category);
    });

    return parents
      .map((parent) => ({
        id: `section:${parent.id}`,
        name: parent.name,
        options: (childrenByParent[parent.id] || []).map((child) => ({
          id: String(child.id),
          name: child.name,
          parentName: parent.name,
        })),
      }))
      .filter((section) => section.options.length > 0);
  }, [categories, sections]);

  const createGroups = useMemo(
    () => categories
      .filter((category) => !category.parent_id)
      .map((group) => ({
        id: String(group.id),
        name: group.name,
      })),
    [categories]
  );

  const searchQuery = search.trim();
  const settledSearchQuery = settledSearch.trim();
  const hasSearchQuery = Boolean(searchQuery);
  const isSearchSettled = searchQuery === settledSearchQuery;
  const canCreate = allowCreate && createGroups.length > 0;
  const exactCategoryMatch = useMemo(
    () => hasExactCategoryNameMatch({ query: settledSearchQuery, sections: resolvedSections }),
    [resolvedSections, settledSearchQuery]
  );
  const exactCategoryMatchCurrent = useMemo(
    () => hasExactCategoryNameMatch({ query: searchQuery, sections: resolvedSections }),
    [resolvedSections, searchQuery]
  );
  const directSearchResults = useMemo(() => {
    if (!hasSearchQuery) return [];
    return getDirectCategoryMatches({ query: searchQuery, sections: resolvedSections }).map((option) => ({
      ...option,
      type: 'child',
    }));
  }, [hasSearchQuery, resolvedSections, searchQuery]);
  const settledDirectSearchResults = useMemo(
    () => getDirectCategoryMatches({ query: settledSearchQuery, sections: resolvedSections }),
    [resolvedSections, settledSearchQuery]
  );
  const similarSuggestions = useMemo(() => {
    if (!hasSearchQuery || directSearchResults.length > 0) return [];
    return getSimilarCategorySuggestions({ query: searchQuery, sections: resolvedSections }).map((option) => ({
      ...option,
      type: 'child',
      isSuggestion: true,
    }));
  }, [directSearchResults.length, hasSearchQuery, resolvedSections, searchQuery]);
  const hasStrongMatch = useMemo(
    () => hasStrongCategoryMatch({ query: settledSearchQuery, sections: resolvedSections }),
    [resolvedSections, settledSearchQuery]
  );
  const hasStrongMatchCurrent = useMemo(
    () => hasStrongCategoryMatch({ query: searchQuery, sections: resolvedSections }),
    [resolvedSections, searchQuery]
  );
  const createActionQuery = searchQuery || settledSearchQuery;
  const settledCreateEligible = canCreate
    && isSearchSettled
    && settledSearchQuery.length >= MIN_CREATE_QUERY_LENGTH
    && !exactCategoryMatch
    && !hasStrongMatch
    && settledDirectSearchResults.length === 0;
  const canKeepCreateVisibleForCurrentQuery = canCreate
    && searchQuery.length >= MIN_CREATE_QUERY_LENGTH
    && !exactCategoryMatchCurrent
    && !hasStrongMatchCurrent
    && directSearchResults.length === 0;
  const showCreateAction = settledCreateEligible
    || (hasLatchedCreateState && canKeepCreateVisibleForCurrentQuery);
  const shellStyle = useMemo(() => {
    const nextStyle = {
      ...(forceDarkMode ? REVIEW_PICKER_THEME_VARS : null),
      ...(style || {}),
    };
    if (nextStyle.height == null) {
      nextStyle.height = DEFAULT_PICKER_HEIGHT;
    }
    if (nextStyle.maxHeight == null) {
      nextStyle.maxHeight = `min(${DEFAULT_PICKER_HEIGHT}px, calc(100vh - 2rem))`;
    }
    return nextStyle;
  }, [forceDarkMode, style]);

  useEffect(() => {
    createCategoryIntentRef.current = null;
  }, [actorUserId, householdId]);

  // Build flat list for keyboard navigation
  const flatList = useMemo(() => {
    const items = [];

    if (hasSearchQuery) {
      if (directSearchResults.length > 0) {
        return directSearchResults;
      }
      if (similarSuggestions.length > 0) {
        return [
          { id: 'header:similar', name: 'Similar categories', type: 'header' },
          ...similarSuggestions,
        ];
      }
      return [];
    } else {
      // Grouped mode: show all categories grouped by parent
      resolvedSections.forEach((section) => {
        items.push({ id: section.id, name: section.name, type: 'header' });
        section.options.forEach((option) => {
          items.push({
            ...option,
            id: option.id,
            name: option.name,
            parentName: option.parentName || section.name,
            type: 'child'
          });
        });
      });
    }

    return items;
  }, [directSearchResults, hasSearchQuery, resolvedSections, similarSuggestions]);

  // Get selectable items (exclude headers)
  const selectableItems = useMemo(() =>
    flatList.filter(item => item.type === 'child'),
    [flatList]
  );

  // Auto-focus search input on mount
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Reset highlight when search changes
  useEffect(() => {
    setHighlightedIndex(0);
    setPreviewValue(String(value || ''));
  }, [search, value]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettledSearch(searchQuery);
    }, SEARCH_SETTLE_MS);

    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    setPreviewValue(String(value || ''));
  }, [value]);

  useEffect(() => {
    onPreviewValueChange?.(previewValue);
  }, [onPreviewValueChange, previewValue]);

  useEffect(() => {
    if (!isCreateMode) {
      setCreateError('');
    }
  }, [isCreateMode]);

  useEffect(() => {
    if (!searchQuery) {
      setHasLatchedCreateState(false);
      return;
    }

    if (settledCreateEligible) {
      setHasLatchedCreateState(true);
      return;
    }

    if (!canKeepCreateVisibleForCurrentQuery) {
      setHasLatchedCreateState(false);
    }
  }, [canKeepCreateVisibleForCurrentQuery, searchQuery, settledCreateEligible]);

  useEffect(() => {
    if (!showCreateAction) {
      setIsCreateMode(false);
      setCreateName('');
      setCreateError('');
    }
  }, [showCreateAction]);

  const openCreateMode = useCallback(() => {
    if (!showCreateAction) return;
    setIsCreateMode(true);
    setCreateName(createActionQuery.trim());
    setSelectedParentId((current) => current || createGroups[0]?.id || '');
    setCreateError('');
  }, [createActionQuery, createGroups, showCreateAction]);

  const closeCreateMode = useCallback(() => {
    if (isCreating) return;
    setIsCreateMode(false);
    setCreateName('');
    setCreateError('');
  }, [isCreating]);

  const handleCreateCategory = useCallback(async () => {
    const trimmedName = createName.trim();
    if (!trimmedName) {
      setCreateError('Category name is required');
      return;
    }
    if (!selectedParentId) {
      setCreateError('Choose a top-level group');
      return;
    }
    if (!authenticatedFetch || !householdId) {
      setCreateError('Category creation is unavailable right now');
      return;
    }

    setIsCreating(true);
    setCreateError('');
    const payload = {
      householdId,
      name: trimmedName,
      parentId: Number(selectedParentId),
      keywords: [],
    };
    const intent = getOrCreateMutationIntent(createCategoryIntentRef, {
      scope: 'category-picker-create',
      payload,
      storageKey: createCategoryIntentStorageKey,
    });

    try {
      const response = await authenticatedFetch(`${API_URL}/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': intent.key,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      const createOutcome = resolveCategoryCreateMutationOutcome({
        responseOk: response.ok,
        data,
        parentName: createGroups.find((group) => String(group.id) === String(selectedParentId))?.name || '',
      });

      if (!createOutcome.success) {
        const contractError = createMutationContractError({
          response,
          data,
          fallbackMessage: createOutcome.errorMessage,
        });
        if (!shouldRetainMutationIntent(contractError)) {
          clearMutationIntent(createCategoryIntentRef, createCategoryIntentStorageKey);
        }
        setCreateError(createOutcome.errorMessage);
        return;
      }
      clearMutationIntent(createCategoryIntentRef, createCategoryIntentStorageKey);

      const nextSelectionState = buildCreatedCategorySelectionState({
        categories,
        sections: resolvedSections,
        createdCategory: createOutcome.createdCategory,
      });
      onCategoryCreated?.(nextSelectionState.createdCategory);
      const result = await onChange(String(createOutcome.createdCategory?.id ?? data?.category?.id ?? ''), {
        categoryCatalog: nextSelectionState.categoryCatalog,
        sections: nextSelectionState.sections,
        createdCategory: nextSelectionState.createdCategory,
      });
      if (result !== false) {
        onClose();
      }
    } catch (error) {
      if (!shouldRetainMutationIntent(error)) {
        clearMutationIntent(createCategoryIntentRef, createCategoryIntentStorageKey);
      }
      setCreateError(resolveCategoryCreateMutationOutcome({
        responseOk: false,
        data: null,
      }).errorMessage);
    } finally {
      setIsCreating(false);
    }
  }, [authenticatedFetch, categories, createCategoryIntentStorageKey, createGroups, createName, householdId, onCategoryCreated, onChange, onClose, resolvedSections, selectedParentId]);

  // Scroll highlighted item into view
  useEffect(() => {
    const highlightedItem = selectableItems[highlightedIndex];
    if (!highlightedItem) return;

    const itemIndex = flatList.findIndex(item =>
      item.type === 'child' && String(item.id) === String(highlightedItem.id)
    );
    const element = itemRefs.current[itemIndex];

    if (element && element.scrollIntoView) {
      element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedIndex, flatList, selectableItems]);

  const handleSelect = useCallback(async (categoryId) => {
    const result = await onChange(String(categoryId), {
      categoryCatalog: categories,
      sections: resolvedSections,
    });
    if (result !== false) {
      onClose();
    }
  }, [categories, onChange, onClose, resolvedSections]);

  const handleKeyDown = useCallback((e) => {
    const target = e.target;
    const isTextInput = target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement;

    if (isCreateMode && isTextInput && target !== searchInputRef.current) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleCreateCategory();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const nextIndex = Math.min(prev + 1, selectableItems.length - 1);
          setPreviewValue(String(selectableItems[nextIndex]?.id || value || ''));
          return nextIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => {
          const nextIndex = Math.max(prev - 1, 0);
          setPreviewValue(String(selectableItems[nextIndex]?.id || value || ''));
          return nextIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectableItems[highlightedIndex]) {
          handleSelect(selectableItems[highlightedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        onClose();
        break;
      default:
        break;
    }
  }, [handleCreateCategory, handleSelect, highlightedIndex, isCreateMode, onClose, selectableItems, value]);

  return (
    <div
      data-testid="category-picker"
      className="flex w-full flex-col overflow-hidden rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-surface)] shadow-[var(--shadow-card)] animate-fade-in"
      style={shellStyle}
      onKeyDown={handleKeyDown}
    >
      {/* Search input */}
      <div className="shrink-0 border-b border-[var(--color-border)] p-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--color-text-muted)]" />
          <input
            ref={searchInputRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] py-2 pl-9 pr-3 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] transition-colors focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]/40"
          />
        </div>
      </div>

      {/* Category list */}
      <div
        ref={listRef}
        className="flex-1 min-h-0 overflow-y-auto py-1"
      >
        {showCreateAction ? (
          <div className="px-2 pb-2">
            <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface-row)] p-2">
              {isCreateMode ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                      Create category
                    </p>
                    <input
                      type="text"
                      value={createName}
                      onChange={(event) => setCreateName(event.target.value)}
                      placeholder="New category name"
                      className="mt-2 w-full rounded-lg border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-accent)] focus:outline-none focus:ring-1 focus:ring-[var(--color-accent)]/40"
                    />
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-[var(--color-text-primary)]">
                      Assign to top-level group
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {createGroups.map((group) => {
                        const isSelected = String(selectedParentId) === String(group.id);
                        const GroupIcon = getCreateGroupIcon(group.name);
                        return (
                          <button
                            key={group.id}
                            type="button"
                            onClick={() => setSelectedParentId(group.id)}
                            className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-medium transition-colors ${
                              isSelected
                                ? 'border-[var(--color-accent)] bg-[var(--color-accent)]/12 text-[var(--color-accent)]'
                                : 'border-[var(--color-border-strong)] bg-[var(--color-surface-input)] text-[var(--color-text-secondary)] hover:border-[var(--color-accent)]/40 hover:text-[var(--color-text-primary)]'
                            }`}
                          >
                            <span className="flex h-5 w-5 shrink-0 items-center justify-center" aria-hidden="true">
                              <GroupIcon
                                className={`h-4 w-4 ${isSelected ? 'text-[var(--color-accent)]' : 'text-[var(--color-text-muted)]'}`}
                                strokeWidth={1.8}
                              />
                            </span>
                            {group.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {createError ? (
                    <p className="text-xs text-rose-500">{createError}</p>
                  ) : null}

                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={closeCreateMode}
                      disabled={isCreating}
                      className="h-9 px-4 text-sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleCreateCategory}
                      disabled={isCreating}
                      className="h-9 px-4 text-sm"
                    >
                      {isCreating ? 'Creating...' : 'Create category'}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={openCreateMode}
                  className="w-full rounded-lg border border-dashed border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-3 py-2 text-left text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-accent)]/45 hover:text-[var(--color-text-primary)]"
                >
                  Create "{createActionQuery.trim()}"
                </button>
              )}
            </div>
          </div>
        ) : null}

        {flatList.length === 0 ? (
          <div className="px-3 py-4 text-center text-sm text-[var(--color-text-muted)]">
            {emptyMessage}
          </div>
        ) : (
          flatList.map((item, index) => {
            if (item.type === 'header') {
              return (
                <div
                  key={`header-${item.id}`}
                  ref={el => itemRefs.current[index] = el}
                  className="bg-[var(--color-surface-row)] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-muted)]"
                >
                  {item.name}
                </div>
              );
            }

            const isSelected = String(value || '') === String(item.id);
            const selectableIndex = selectableItems.findIndex(s => String(s.id) === String(item.id));
            const isHighlighted = selectableIndex === highlightedIndex;

            return (
              <button
                key={`child-${item.id}`}
                ref={el => itemRefs.current[index] = el}
                type="button"
                onClick={() => handleSelect(item.id)}
                onMouseEnter={() => {
                  setHighlightedIndex(selectableIndex);
                  setPreviewValue(String(item.id));
                }}
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
                  isHighlighted
                    ? 'bg-[var(--color-surface-selected-hover)] text-[var(--color-text-primary)]'
                    : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-hover)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                <span className="flex items-center gap-2.5">
                  <PickerOptionIcon
                    option={item}
                    sectionName={item.parentName}
                    className={isHighlighted ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-muted)]'}
                  />
                  {search.trim() ? (
                    <>
                      <span className="text-[var(--color-text-muted)]">{item.parentName}</span>
                      <ChevronRight className="h-3 w-3 text-[var(--color-text-muted)]" />
                      <span>{item.name}</span>
                    </>
                  ) : (
                    <span>{item.name}</span>
                  )}
                </span>
                {isSelected && (
                  <Check className="h-4 w-4 text-[var(--color-accent)]" />
                )}
              </button>
            );
          })
        )}
      </div>

      {footer ? (
        <div className="shrink-0 border-t border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2">
          {footer}
        </div>
      ) : null}

      {/* Keyboard hints */}
      <div className="shrink-0 border-t border-[var(--color-border)] bg-[var(--color-surface-row)] px-3 py-2">
        <div className="flex items-center gap-3 text-xs text-[var(--color-text-muted)]">
          <span><kbd className="rounded bg-[var(--color-surface-input)] px-1.5 py-0.5 text-[var(--color-text-secondary)]">↑↓</kbd> navigate</span>
          <span><kbd className="rounded bg-[var(--color-surface-input)] px-1.5 py-0.5 text-[var(--color-text-secondary)]">↵</kbd> select</span>
          <span><kbd className="rounded bg-[var(--color-surface-input)] px-1.5 py-0.5 text-[var(--color-text-secondary)]">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
};

/**
 * CategoryPickerTrigger - The button/input that opens the picker
 */
const CategoryPickerTrigger = ({
  categories,
  sections = null,
  value,
  onChange,
  placeholder = 'Select category...',
  disabled = false,
  containerClassName = '',
  buttonClassName = '',
  showChevron = false,
  menuMinWidth,
  pickerZIndex,
  trapFocus = false,
  autoOpen = false,
  onOpenChange,
  searchPlaceholder = 'Search categories...',
  emptyMessage = 'No categories found',
  renderPickerFooter,
  allowCreate = false,
  householdId = null,
  actorUserId = null,
  authenticatedFetch = null,
  onCategoryCreated = null,
  forceDarkMode = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [previewValue, setPreviewValue] = useState(String(value || ''));
  const [optimisticCategories, setOptimisticCategories] = useState([]);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const pickerRef = useRef(null);

  const setOpen = useCallback((nextOpen) => {
    setIsOpen(nextOpen);
    onOpenChange?.(nextOpen);
  }, [onOpenChange]);

  useFocusTrap(pickerRef, isOpen && trapFocus, () => setOpen(false), triggerRef);

  // Focus only after the anchored portal is visible, independent of animation
  // timing. Other picker contexts retain their existing focus behavior.
  const focusPositionedPicker = useCallback(() => {
    if (trapFocus) pickerRef.current?.querySelector('input')?.focus();
  }, [trapFocus]);

  useEffect(() => {
    if (!autoOpen || disabled) return;
    setOpen(true);
  }, [autoOpen, disabled, setOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setPreviewValue(String(value || ''));
  }, [isOpen, value]);

  const effectiveCategories = useMemo(
    () => mergeOptimisticCategories(categories, optimisticCategories),
    [categories, optimisticCategories]
  );

  const effectiveSections = useMemo(() => {
    if (!Array.isArray(sections) || sections.length === 0) return sections;
    return mergeOptimisticSections({
      baseSections: sections,
      optimisticCategories,
      categoryCatalog: effectiveCategories,
    });
  }, [effectiveCategories, optimisticCategories, sections]);

  const handleOptimisticCategoryCreated = useCallback((category) => {
    setOptimisticCategories((prev) => mergeOptimisticCategories(prev, [category]));
    onCategoryCreated?.(category);
  }, [onCategoryCreated]);

  const handleChange = useCallback((nextValue, metadata = {}) => onChange(nextValue, {
    ...metadata,
    categoryCatalog: metadata.categoryCatalog || effectiveCategories,
    sections: metadata.sections || effectiveSections,
  }), [effectiveCategories, effectiveSections, onChange]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e) => {
      const inTrigger = containerRef.current && containerRef.current.contains(e.target);
      const inPicker = pickerRef.current && pickerRef.current.contains(e.target);
      if (!inTrigger && !inPicker) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setOpen]);

  // Get display text
  const displayText = useMemo(() => {
    if (!value) return placeholder;
    if (Array.isArray(effectiveSections) && effectiveSections.length > 0) {
      for (const section of effectiveSections) {
        const option = section.options.find((candidate) => String(candidate.id) === String(value));
        if (option) return `${option.parentName || section.name} → ${option.name || option.label || ''}`;
      }
    }
    const cat = effectiveCategories.find((candidate) => Number(candidate.id) === Number.parseInt(value, 10));
    if (!cat) return placeholder;
    const parent = effectiveCategories.find((candidate) => Number(candidate.id) === Number(cat.parent_id));
    return parent ? `${parent.name} → ${cat.name}` : cat.name;
  }, [effectiveCategories, effectiveSections, placeholder, value]);

  const hasValue = Boolean(displayText && displayText !== placeholder);

  return (
    <div ref={containerRef} className={`relative ${containerClassName}`.trim()}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => {
          if (disabled) return;
          setOpen(!isOpen);
        }}
        disabled={disabled}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-[var(--color-surface-input)] px-3 py-2 text-left text-sm transition-colors focus:outline-none ${
          isOpen
            ? 'border-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/50'
            : 'border-[var(--color-border-strong)] hover:border-[var(--color-accent)]/45'
        } ${hasValue ? 'text-[var(--color-text-primary)]' : 'text-[var(--color-text-muted)]'} ${disabled ? 'cursor-not-allowed opacity-60' : ''} ${buttonClassName}`}
      >
        <span className="truncate">{displayText}</span>
        {showChevron && (
          <ChevronRight className="h-4 w-4 rotate-90 text-[var(--color-text-muted)]" />
        )}
      </button>

      <AnchoredOverlay
        open={isOpen}
        anchorRef={containerRef}
        overlayRef={pickerRef}
        align="start"
        offset={6}
        minWidth={menuMinWidth}
        matchWidth
        zIndex={pickerZIndex ?? 50}
        onPositioned={focusPositionedPicker}
      >
        <CategoryPicker
          categories={effectiveCategories}
          sections={effectiveSections}
          value={value}
          onChange={handleChange}
          onClose={() => setOpen(false)}
          searchPlaceholder={searchPlaceholder}
          emptyMessage={emptyMessage}
          footer={renderPickerFooter ? renderPickerFooter(previewValue) : null}
          onPreviewValueChange={setPreviewValue}
          allowCreate={allowCreate}
          householdId={householdId}
          actorUserId={actorUserId}
          authenticatedFetch={authenticatedFetch}
          onCategoryCreated={handleOptimisticCategoryCreated}
          forceDarkMode={forceDarkMode}
        />
      </AnchoredOverlay>
    </div>
  );
};

export default CategoryPickerTrigger;
export { CategoryPicker };
